import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Box from 'ui-box';
import { CompanyThumbnail } from '../companies';
import {
  Button,
  Container,
  defaultColors,
  defaultFills,
  Heading,
  Icon,
  KeyValue,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  PageLoader,
  Popover,
  Section,
  Spinner,
  Tag,
  useAlert,
  useToast,
} from '../../npaper';
import { PageHeader } from '../../npaper/PageHeader';
import { PurchaseOrderDetailsDialog } from './PurchaseOrderDetailsDialog';
import {
  IPurchaseOrder,
  PurchaseOrderStatus,
} from '../../stores/PurchaseOrderStore';
import { ICompany, useStores } from '../../stores';
import { observer } from 'mobx-react-lite';
import { PurchaseOrderLinesTable } from './PurchaseOrderItemsTable';
import { PurchaseOrderStatusTag } from './PurchaseOrderStatusTag';
import { SaleThumbnail } from '../sales';
import { SendItemSideSheet } from '../../npaper/SendItemSideSheet';
import { TimeLine } from '../time-line/TimeLine';

export const PurchaseOrderPage = observer(() => {
  const intl = useIntl();
  const toaster = useToast();
  const history = useHistory();
  const { purchaseOrderStore } = useStores();

  const match = useRouteMatch<{ id: string }>();
  const id = match.params.id;

  const [showDetails, setShowDetails] = useState(false);

  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>();
  const [notFound, setNotFound] = useState(false);
  const [sendPurchaseOrder, setSendPurchaseOrder] = useState(false);

  const deleteAlert = useAlert({
    intent: 'danger',
    title: intl.formatMessage({
      defaultMessage: 'Delete purchase order',
      description: 'Delete purchase order alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to delete the purchase order? This action cannot be undone.',
      description: 'Delete purchase order alert description',
    }),
    icon: 'trash',
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Delete',
      description: 'Delete purchase order alert confirm button text',
    }),
  });

  const sendPurchaseOrderMessage = intl.formatMessage({
    defaultMessage: 'Send purchase order',
  });

  const purchaseOrderDeletedMessage = intl.formatMessage({
    defaultMessage: 'Purchase order was deleted',
    description: 'Purchase order deleted toast message',
  });

  useEffect(() => {
    let isSub = true;
    setPurchaseOrder(undefined);
    purchaseOrderStore
      .getItem(id, true)
      .then((purchaseOrder) => isSub && setPurchaseOrder(purchaseOrder))
      .catch(() => isSub && setNotFound(true));
    return () => {
      isSub = false;
    };
  }, [id, purchaseOrderStore]);

  return !purchaseOrder ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={majorScale(2)}
            color={defaultFills.danger[500]}
          />
          <Heading size={400}>Kunne ikke finde indkøbsordre</Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer>
      <PurchaseOrderDetailsDialog
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
        purchaseOrder={purchaseOrder}
      />
      {purchaseOrder.supplier && (
        <SendItemSideSheet
          isShown={sendPurchaseOrder}
          onClose={() => setSendPurchaseOrder(false)}
          pdfUrl={`${purchaseOrderStore.apiUrl}${purchaseOrderStore.apiPath}/${purchaseOrder.id}/pdf/`}
          filename={() => `po-${purchaseOrder.purchase_order_number}.pdf`}
          title={sendPurchaseOrderMessage}
          canAddCC={false}
          sendTitle={sendPurchaseOrderMessage}
          downloadTitle={intl.formatMessage({
            defaultMessage: 'Download purchase order',
          })}
          onDownload={async () => {
            if (purchaseOrder.status === PurchaseOrderStatus.DRAFT) {
              await purchaseOrderStore.placePurchaseOrder(purchaseOrder);
            }
          }}
          onSubmit={async (values) => {
            try {
              await purchaseOrderStore.sendPurchaseOrder(
                purchaseOrder,
                values.recipients,
                values.language,
                values.addResponsibleBCC
              );
              toaster.addToast({
                intent: 'success',
                message: intl.formatMessage({
                  defaultMessage: 'Email successfully sent to recipients',
                }),
              });
              setSendPurchaseOrder(false);
            } catch (error) {
              toaster.showDefaultError(error);
            }
          }}
          recipientCompany={purchaseOrder.supplier}
          defaultValue={{
            recipients: (purchaseOrder.supplier as ICompany).email
              ? [(purchaseOrder.supplier as ICompany).email!]
              : undefined,
          }}
        />
      )}

      <Box
        backgroundColor={
          purchaseOrder.status === PurchaseOrderStatus.DRAFT
            ? defaultFills.normal[400]
            : purchaseOrder.status === PurchaseOrderStatus.OPEN
            ? defaultColors.blue[500]
            : defaultColors.green[500]
        }
        position="fixed"
        left={0}
        right={0}
        top={0}
        height={minorScale(1)}
      />
      <Container>
        <PageHeader
          title={
            <Box display="flex" alignItems="flex-end">
              <Heading size={600} marginRight={majorScale(1)}>
                <span className="font-mono">{id}</span> til{' '}
                {purchaseOrder.supplier ? (
                  purchaseOrder.supplier.name
                ) : (
                  <Spinner size={16} />
                )}
              </Heading>
              <PurchaseOrderStatusTag purchaseOrder={purchaseOrder} />
              {purchaseOrder.drop_shipping && (
                <Tag intent="warning">
                  <FormattedMessage defaultMessage="Drop shipping" />
                </Tag>
              )}
            </Box>
          }
          type="Indkøbsordre"
          typeIcon="truck"
          id={id}
          actions={
            <>
              <Button
                iconLeft="send-message"
                marginRight={majorScale(1)}
                onClick={() => setSendPurchaseOrder(true)}
              >
                Send til leverandør
              </Button>
              <Popover
                placement="bottom-end"
                content={(close) => (
                  <Menu>
                    <MenuDivider text="Handlinger" />
                    {purchaseOrder.status !== PurchaseOrderStatus.DRAFT &&
                      purchaseOrder.status !==
                        PurchaseOrderStatus.FINALIZED && (
                        <MenuItem
                          text="Markér resten som modtaget"
                          icon="endorsed"
                          onClick={() => {
                            close();
                            purchaseOrderStore
                              .markAllComplete(purchaseOrder)
                              .then(() =>
                                toaster.addToast({
                                  intent: 'success',
                                  message: intl.formatMessage({
                                    defaultMessage:
                                      'All items has been marked as complete',
                                  }),
                                })
                              )
                              .catch(toaster.showDefaultError);
                          }}
                        />
                      )}
                    {purchaseOrder.status === PurchaseOrderStatus.DRAFT && (
                      <MenuItem
                        text="Markér som bestilt"
                        icon="endorsed"
                        onClick={() => {
                          close();
                          purchaseOrderStore
                            .placePurchaseOrder(purchaseOrder)
                            .then(() => {
                              toaster.addToast({
                                intent: 'success',
                                message: intl.formatMessage({
                                  defaultMessage:
                                    'Purchase order marked as ordered',
                                  description:
                                    'Purchase order placed success toast message',
                                }),
                              });
                            })
                            .catch(toaster.showDefaultError);
                        }}
                      />
                    )}
                    <MenuItem
                      text="Send til leverandør"
                      icon="send-message"
                      onClick={() => {
                        close();
                        setSendPurchaseOrder(true);
                      }}
                    />
                    <MenuDivider text="Indkøbsordre" />
                    <MenuItem
                      text="Rediger detaljer"
                      onClick={() => {
                        setShowDetails(true);
                        close();
                      }}
                    />
                    {purchaseOrder.status === PurchaseOrderStatus.DRAFT && (
                      <>
                        <MenuDivider />
                        <MenuItem
                          text="Slet indkøbsordre"
                          icon="trash"
                          intent="danger"
                          onClick={() => {
                            close();
                            deleteAlert.show(async () => {
                              await purchaseOrderStore
                                .deleteItem(purchaseOrder)
                                .then(() => {
                                  toaster.addToast({
                                    intent: 'success',
                                    message: purchaseOrderDeletedMessage,
                                  });
                                  history.replace('/purchase-orders');
                                })
                                .catch(toaster.showDefaultError);
                            });
                          }}
                        />
                      </>
                    )}
                  </Menu>
                )}
              >
                <Button iconRight="chevron-down">Handlinger</Button>
              </Popover>
            </>
          }
        />

        <Section
          title="Detaljer"
          actions={
            <Button
              minimal
              iconRight="edit"
              onClick={() => setShowDetails(true)}
            >
              Rediger
            </Button>
          }
        >
          <Box display="flex">
            <Box width="50%">
              <KeyValue
                keyName="Leverandør"
                value={
                  purchaseOrder.supplier ? (
                    <CompanyThumbnail company={purchaseOrder.supplier} />
                  ) : (
                    <Spinner size={16} />
                  )
                }
              />
              <KeyValue
                keyName="Leveringsadresse"
                value={purchaseOrder.delivery_address.description}
              />
              <KeyValue keyName="Att." value={purchaseOrder.attention} />
              <KeyValue
                keyName="Relaterede salg"
                value={
                  purchaseOrder.related_sales.length > 0 ? (
                    <>
                      {purchaseOrder.related_sales.map((s, i) =>
                        s ? (
                          <SaleThumbnail
                            key={s.id}
                            sale={s}
                            marginRight={majorScale(2)}
                          />
                        ) : (
                          <Spinner key={'index-' + i} size={16} />
                        )
                      )}
                    </>
                  ) : (
                    ''
                  )
                }
                placeholder="Ingen relaterede"
              />
              {/*<KeyValue
                keyName="Salgspris"
                mono
                value={intl.formatNumber(purchaseOrder.sales_price || 0, {
                  format: 'currency',
                  currency: purchaseOrder.sales_price_currency,
                })}
              />*/}
              <KeyValue
                keyName="Kostpris"
                mono
                value={intl.formatNumber(purchaseOrder.cost_price, {
                  format: 'currency',
                  currency: purchaseOrder.cost_price_currency,
                })}
              />
              {/*<KeyValue
                keyName="Dækningsgrad"
                mono
                value={intl.formatNumber(
                  purchaseOrder.contributionMarginRatio,
                  {
                    style: 'percent',
                  }
                )}
                />*/}
            </Box>
            <Box
              width="50%"
              paddingLeft="16px"
              borderLeft={`2px solid ${defaultColors.gray[100]}`}
              alignSelf="flex-start"
            >
              <KeyValue
                keyName="Indkøbsordre nr."
                value={purchaseOrder.purchase_order_number}
                mono
              />
              <KeyValue
                keyName="Reference"
                mono
                value={purchaseOrder.reference}
              />
              <KeyValue
                keyName="Beskrivelse"
                value={purchaseOrder.description}
              />
              <KeyValue
                keyName="Tags"
                placeholder="Ingen tags"
                value={
                  purchaseOrder.tags.length > 0
                    ? purchaseOrder.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Box>
          </Box>
        </Section>

        <Section title="Linjer">
          <PurchaseOrderLinesTable purchaseOrder={purchaseOrder} />
        </Section>

        <Section title={intl.formatMessage({ defaultMessage: 'Timeline' })}>
          <TimeLine
            timelineBaseUrl={`${purchaseOrderStore.apiPath}/${purchaseOrder.id}/timeline/`}
          />
        </Section>
      </Container>
    </PageContainer>
  );
});
